import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  type AuthTokens,
  getStoredTokens,
  setStoredTokens,
  clearStoredTokens,
  apiClient,
  TOKEN_STORAGE_KEY,
} from '../api/client';
import { parseApiError } from '../api/errors';

export interface UserProfile {
  id: string;
  email: string;
  is_active: boolean;
  email_verified?: boolean;
  email_verified_at?: string | null;
  max_active_watches: number;
}

export interface AuthContextType {
  user: UserProfile | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  resendVerification: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const defaultAuthContext: AuthContextType = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isEmailVerified: false,
  isLoading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  resendVerification: async () => {},
  refreshProfile: async () => {},
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tokens, setTokensState] = useState<AuthTokens | null>(() => getStoredTokens());
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchProfile = useCallback(async () => {
    try {
      const { data, error } = await apiClient.GET('/api/v1/account/me', {});
      if (error || !data) {
        setUser(null);
        return;
      }
      setUser(data as unknown as UserProfile);
    } catch {
      setUser(null);
    }
  }, []);

  // Initial session hydration
  useEffect(() => {
    const initSession = async () => {
      const currentTokens = getStoredTokens();
      if (currentTokens?.accessToken) {
        setTokensState(currentTokens);
        await fetchProfile();
      } else {
        setTokensState(null);
        setUser(null);
      }
      setIsLoading(false);
    };

    initSession();
  }, [fetchProfile]);

  // Handle session expiration, cross-tab sync, and window focus
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleSessionExpired = () => {
      clearStoredTokens();
      setTokensState(null);
      setUser(null);
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_STORAGE_KEY) {
        if (!e.newValue) {
          // Cleared in another tab -> logout
          setTokensState(null);
          setUser(null);
        } else {
          try {
            const parsed = JSON.parse(e.newValue) as AuthTokens;
            setTokensState(parsed);
            fetchProfile();
          } catch {
            setTokensState(null);
            setUser(null);
          }
        }
      }
    };

    // Silently re-check profile when tab gains focus (e.g. after confirming verification link)
    const handleFocus = () => {
      if (getStoredTokens()?.accessToken) {
        fetchProfile();
      }
    };

    window.addEventListener('priceloss:session-expired', handleSessionExpired);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('priceloss:session-expired', handleSessionExpired);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchProfile]);

  const login = async (email: string, password: string): Promise<void> => {
    const { data, error } = await apiClient.POST('/api/v1/auth/login', {
      body: { email, password },
    });

    if (error || !data) {
      throw new Error(parseApiError(error).message);
    }

    const newTokens: AuthTokens = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      tokenType: data.token_type,
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    setStoredTokens(newTokens);
    setTokensState(newTokens);
    await fetchProfile();
  };

  const register = async (email: string, password: string): Promise<void> => {
    const { error: regError } = await apiClient.POST('/api/v1/auth/register', {
      body: { email, password },
    });

    if (regError) {
      throw new Error(parseApiError(regError).message);
    }

    // Auto-login upon registration
    await login(email, password);
  };

  const logout = () => {
    clearStoredTokens();
    setTokensState(null);
    setUser(null);
  };

  const resendVerification = async (): Promise<void> => {
    if (!user?.email) return;
    const { error } = await apiClient.POST('/api/v1/auth/resend-verification', {
      body: { email: user.email },
    });
    if (error) {
      throw new Error(parseApiError(error).message);
    }
  };

  const isAuthenticated = !!tokens?.accessToken && !!user;
  const isEmailVerified = Boolean(user?.email_verified || user?.email_verified_at);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated,
        isEmailVerified,
        isLoading,
        login,
        register,
        logout,
        resendVerification,
        refreshProfile: fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  return context || defaultAuthContext;
}
